import type { QueryClient } from '@tanstack/react-query';

/**
 * Helpers for the boot splash greeting copy. Designed to never fetch, never
 * throw, never re-render the splash mid-flight — the resolved name is read
 * once at mount via {@link getFirstNameSync} and stays stable for the
 * lifetime of that splash render.
 */

const STORAGE_KEY = 'allo:last-known-first-name';

const splitFirst = (name: string | undefined): string | undefined => {
  if (!name) {
    return undefined;
  }
  const trimmed = name.trim();
  if (!trimmed) {
    return undefined;
  }
  // Split on whitespace only — hyphenated first names ("Marie-Claire") stay
  // intact. For composite given names ("Anne Marie") we surface the first
  // chunk; acceptable for a greeting.
  return trimmed.split(/\s+/)[0];
};

/**
 * Best-effort sync read of the current user's first name for greeting copy.
 *
 * Resolution order:
 *  1. React Query cache for `/v1/user` — most reliable once the `_app` loader
 *     has run at least once.
 *  2. `localStorage` cache populated on the previous boot (see
 *     {@link persistFirstName}). Covers the cold-boot splash before the
 *     loader has resolved.
 *
 * Returns `undefined` when nothing's available — the caller falls back to
 * the generic welcome string.
 */
export const getFirstNameSync = (queryClient: QueryClient): string | undefined => {
  const cached = queryClient.getQueryData<{ data?: { name?: string } }>(['get', '/v1/user', {}]);
  const fromQuery = splitFirst(cached?.data?.name);
  if (fromQuery) {
    return fromQuery;
  }

  try {
    return splitFirst(localStorage.getItem(STORAGE_KEY) ?? undefined);
  } catch {
    // localStorage can throw in private mode / on quota errors. The greeting
    // just falls back to the generic copy — never block the splash.
    return undefined;
  }
};

/**
 * Persist the user's first name (parsed from a full `name`) so the next
 * boot's splash can greet the user before `/v1/user` is refetched. Called
 * once per loader run from the `_app` route.
 *
 * Treats the value as soft cache: failures are swallowed.
 */
export const persistFirstName = (name: string | undefined): void => {
  const first = splitFirst(name);
  if (!first) {
    return;
  }
  try {
    localStorage.setItem(STORAGE_KEY, first);
  } catch {
    // Best-effort — private mode / quota errors just skip persistence.
  }
};
